// İSG uzmanı portalı — Saha oluştur/düzenle formu (Modal + RHF + Zod). api-contract
// §10.3: `{ branchUuid, name, code?, address? }`. Saha bir şubeye bağlıdır → şube
// seçici (`GET /branches`'ten doldurulan Select) **zorunludur**; şube aynı firmada
// olmalı (backend doğrular, değilse `404 BRANCH_NOT_FOUND`). Firmaya kapsanmış.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  Select,
  type SelectOption,
  type SiteSettingsDto,
  Spinner,
  resolveApiErrorMessage,
  settingsApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { settingsKeys } from '../../lib/settingsKeys';

/** Şube seçicisini tek sayfada besleyecek makul üst sınır (tenant ölçeği). */
const BRANCH_OPTIONS_PAGE_SIZE = 200;

interface SiteFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Verilirse düzenleme; yoksa oluşturma. */
  site?: SiteSettingsDto | null;
  onSuccess?: () => void;
}

function nullable(value: string | undefined): string | null {
  return value && value.trim().length > 0 ? value.trim() : null;
}

export function SiteFormModal({ open, onOpenChange, site, onSuccess }: SiteFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = Boolean(site);

  // Şube seçenekleri — yalnız modal açıkken çekilir (kanonik seçim shape: {value,label}).
  const branchesQuery = useQuery({
    queryKey: settingsKeys.branchOptions(),
    queryFn: () =>
      settingsApi.listBranches({
        page: 1,
        pageSize: BRANCH_OPTIONS_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      }),
    enabled: open,
    staleTime: 60_000,
  });

  const branchOptions: SelectOption[] = useMemo(
    () => (branchesQuery.data?.items ?? []).map((b) => ({ value: b.uuid, label: b.name })),
    [branchesQuery.data],
  );

  const schema = useMemo(
    () =>
      z.object({
        branchUuid: z.string().trim().min(1, t('settings.validation.branchRequired')),
        name: z.string().trim().min(1, t('settings.validation.nameRequired')),
        code: z.string().trim().optional(),
        address: z.string().trim().optional(),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      branchUuid: site?.branchUuid ?? '',
      name: site?.name ?? '',
      code: site?.code ?? '',
      address: site?.address ?? '',
    },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const body = {
        branchUuid: values.branchUuid,
        name: values.name.trim(),
        code: nullable(values.code),
        address: nullable(values.address),
      };
      return site ? settingsApi.updateSite(site.uuid, body) : settingsApi.createSite(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.sites() });
      reset();
      onSuccess?.();
      onOpenChange(false);
    },
  });

  const noBranches = branchesQuery.isSuccess && branchOptions.length === 0;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
        }
        onOpenChange(next);
      }}
      title={isEdit ? t('settings.sites.edit.title') : t('settings.sites.create.title')}
      closeLabel={t('settings.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('settings.common.cancel')}
          </Button>
          <Button type="submit" form="site-form" loading={mutation.isPending} disabled={noBranches}>
            {mutation.isPending ? (
              <Spinner label={t('settings.common.save')} />
            ) : isEdit ? (
              t('settings.common.save')
            ) : (
              t('settings.common.create')
            )}
          </Button>
        </>
      }
    >
      <form
        id="site-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        {noBranches ? <Alert tone="info">{t('settings.sites.noBranches')}</Alert> : null}

        <Field
          htmlFor="site-branch"
          label={t('settings.sites.field.branch')}
          error={formState.errors.branchUuid?.message}
          required
        >
          <Select
            id="site-branch"
            invalid={Boolean(formState.errors.branchUuid)}
            disabled={branchesQuery.isPending || noBranches}
            placeholder={t('settings.sites.branchPlaceholder')}
            options={branchOptions}
            {...register('branchUuid')}
          />
        </Field>

        <Field
          htmlFor="site-name"
          label={t('settings.sites.field.name')}
          error={formState.errors.name?.message}
          required
        >
          <Input id="site-name" invalid={Boolean(formState.errors.name)} {...register('name')} />
        </Field>

        <Field htmlFor="site-code" label={t('settings.sites.field.code')}>
          <Input id="site-code" {...register('code')} />
        </Field>

        <Field htmlFor="site-address" label={t('settings.sites.field.address')}>
          <Input id="site-address" {...register('address')} />
        </Field>
      </form>
    </Modal>
  );
}
